import { ComboBox, IDropdownOption, Spinner, Stack, TextField } from '@fluentui/react';
import { ColumnDto, DataverseConfiguration, DataverseIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_PROJECT_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface IProjectPaneProps {
    config: DataverseConfiguration;
    dataverseTables: IDropdownOption<ColumnDto>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
}

export const ProjectPane = (props: IProjectPaneProps) => {
    const { dataverseUrl, projectTableLogicalName } = props.config;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: projectEntityOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_PROJECT_COLUMNS, dataverseUrl, projectTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: projectTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && projectTableLogicalName),
        suspense: true,
    });

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Project Table"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={projectTableLogicalName ? '' : 'Table required'}
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.projectTableLogicalName = opt.key as string;
                                integration.configuration.projectTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.projectTableLogicalName = '';
                                integration.configuration.projectTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={projectTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            {projectTableLogicalName && !projectEntityOptions && <Spinner />}
            <ComboBox
                label="Project Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.projectIdColumn ? '' : 'Project Id column required'}
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectIdColumn}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Project Name"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.projectNameColumn ? '' : 'Project Name column required'}
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectNameColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectNameColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Metadata"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectMetadataColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectMetadataColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            {/* Add project manager */}
            <ComboBox
                label="Project Manager"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectManagerColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectManagerColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            {/* Add project type */}
            <ComboBox
                label="Is Administrative"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions.filter(o => o.data.attributeType === 'Boolean')}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectIsAdministrativeColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectIsAdministrativeColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            {/* Add project number */}
            <ComboBox
                label="Project Number"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectNumberColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectNumberColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Project Closed"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectClosedColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectClosedColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <TextField
                label={'Project Closed Compare Value'}
                onChange={(e, value) =>
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectClosedValue = value ?? '';
                        }),
                    )
                }
                value={props.config.projectClosedValue ?? ''}
                disabled={projectEntityOptions.length <= 0}
            />
            <ComboBox
                label="Default Work Type"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectDefaultWorkTypeColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectDefaultWorkTypeColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Work Type"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectWorkTypeColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectWorkTypeColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Order"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectOrderColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectOrderColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Priority"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectPriorityColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectPriorityColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Sync Flag"
                allowFreeform
                autoComplete="on"
                options={projectEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectSyncColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={projectEntityOptions.length <= 0}
                selectedKey={props.config.projectSyncColumn ?? ''}
                onRenderOption={onRenderOption}
            />
        </Stack>
    );
};
